import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  FormControl,
  FormLabel,
  InputAdornment,
  MenuItem,
  Select as MuiSelect,
  Paper,
  Tooltip,
} from "@mui/material";
import { classes } from "common/theme/theme";
import { Direction } from "protos/rtc/path_plan";
import { Field, useFormikContext } from "formik";
import { FormikDimensionedNumberInput } from "common/components/FormikDimensionedNumberInput";
import { Select as FormikSelect, TextField } from "formik-mui";
import { NewJobsFormState } from "../FormState";
import { Trans, useTranslation } from "react-i18next";
import { typedPath } from "typed-path";
import { useSelf } from "portal/state/store";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import React, { useMemo, useState } from "react";
import WarningIcon from "@mui/icons-material/WarningAmberOutlined";

const S = typedPath<NewJobsFormState>();

interface PassSettingsSectionProps {
  enablePlantingHeadingOffset: boolean;
}

export const PassSettingsSection: React.FC<PassSettingsSectionProps> = ({
  enablePlantingHeadingOffset,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(true);
  const { measurementSystem } = useSelf();
  const { values, setFieldValue, setFieldTouched, validateForm } =
    useFormikContext<NewJobsFormState>();

  // the row heading widget needs not to rerender every time the state of the
  // form changes; since the Trans component regenerates the DOM, this results
  // in a heading offset widget that only lets you enter one digit at a time
  const rowHeadingWidget = useMemo(
    () => (
      <Trans
        i18nKey="views.autotractor.jobCreation.step3ConfigurePreview.groundPrep.rowHeadingInput"
        components={{
          offset: (
            <FormControl className="flex-1">
              <Field
                component={TextField}
                type="number"
                aria-labelledby="row-offset-value-label"
                name={S.groundPrep.rowHeadingOffset.$path}
                size="small"
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        {t("utils.units.degLong")}
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </FormControl>
          ),
          basis: (
            <FormControl className="flex-1">
              <Field
                component={FormikSelect}
                name={S.groundPrep.rowHeadingMode.$path}
                disabled={!enablePlantingHeadingOffset}
                size="small"
              >
                <MenuItem value="north">
                  {t(
                    "views.autotractor.jobCreation.step3ConfigurePreview.groundPrep.rowHeadingBasisNorth"
                  )}
                </MenuItem>
                <MenuItem value="planting">
                  {t(
                    "views.autotractor.jobCreation.step3ConfigurePreview.groundPrep.rowHeadingBasisPlantingHeading"
                  )}
                </MenuItem>
              </Field>
            </FormControl>
          ),
        }}
      />
    ),
    [enablePlantingHeadingOffset, t]
  );

  return (
    <Accordion
      className="rounded-lg"
      expanded={isOpen}
      onChange={() => setIsOpen(!isOpen)}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        className={classes(
          "border-x border-t border-solid border-carbon-gray-light bg-inherit",
          {
            "rounded-t-lg": isOpen,
            "border-b rounded-lg": !isOpen,
          }
        )}
      >
        {t(
          "views.autotractor.jobCreation.step3ConfigurePreview.pathPlanningSettingsHeader"
        )}
      </AccordionSummary>
      <AccordionDetails
        className={
          "border border-solid border-carbon-gray-light bg-carbon-gray-lightest dark:bg-carbon-gray-dark " +
          "rounded-b-lg grid grid-flow-row grid-cols-4 items-center gap-4"
        }
      >
        <Paper variant="outlined" className="bg-inherit col-span-2 p-2">
          <FormLabel id="row-heading-label" className="align-middle flex gap-2">
            <span>{t("models.pathPlanning.rowHeading")}</span>
            {!enablePlantingHeadingOffset && (
              <Tooltip
                title={t(
                  "views.autotractor.jobCreation.step3ConfigurePreview.groundPrep.plantingHeadingDisabledTooltip"
                )}
              >
                <WarningIcon fontSize="small" color="warning" />
              </Tooltip>
            )}
          </FormLabel>
          <div className="flex flex-row items-baseline gap-2">
            {rowHeadingWidget}
          </div>
        </Paper>
        <FormControl>
          <FormLabel
            id="row-overlap-label"
            className="flex flex-row gap-2 items-center"
          >
            <span>
              {t(
                "views.autotractor.jobCreation.step3ConfigurePreview.groundPrep.workingOverlap"
              )}
            </span>
            <Tooltip
              title={t(
                "views.autotractor.jobCreation.step3ConfigurePreview.groundPrep.workingOverlapTooltip"
              )}
              placement="top"
            >
              <InfoOutlinedIcon fontSize="small" />
            </Tooltip>
          </FormLabel>
          <FormikDimensionedNumberInput
            className="w-full"
            label=""
            name={S.groundPrep.workingOverlapM.$path}
            dimensionedNumberInputProps={{
              storedUnit: "m",
              displayUnits: {
                imperial: "ft",
                metric: "m",
              },
              localSystem: measurementSystem,
              minStoredValue: 0,
              textFieldProps: {
                slotProps: {
                  htmlInput: {
                    min: 0,
                    step: 0.1,
                  },
                },
              },
            }}
            aria-labelledby="row-overlap-label"
          />
        </FormControl>
        <FormControl>
          <FormLabel id="turn-direction-label">
            {t("models.pathPlanning.turnDirection")}
          </FormLabel>
          {/* work around alignment sloppiness from #4607 */}
          <FormControl className="mt-0">
            <MuiSelect
              value={values.groundPrep.turnDirection}
              onChange={(e) => {
                const path = S.groundPrep.turnDirection.$path;

                // work around https://github.com/jaredpalmer/formik/issues/1191
                // and https://github.com/jaredpalmer/formik/issues/2083
                setFieldValue(path, e.target.value).finally(() =>
                  setFieldTouched(path, true)
                );
                validateForm();
              }}
              aria-labelledby="turn-direction-label"
              size="small"
            >
              <MenuItem value={Direction.CW}>
                {t("utils.units.cwLong")}
              </MenuItem>
              <MenuItem value={Direction.CCW}>
                {t("utils.units.ccwLong")}
              </MenuItem>
            </MuiSelect>
          </FormControl>
        </FormControl>
        <FormControl>
          <FormLabel
            id="headland-width-label"
            className="align-middle flex gap-2"
          >
            <span>{t("models.pathPlanning.headlandWidth")}</span>
            <Tooltip
              title={t(
                "views.autotractor.jobCreation.step3ConfigurePreview.groundPrep.headlandWidthTooltip"
              )}
              placement="top"
            >
              <InfoOutlinedIcon fontSize="small" />
            </Tooltip>
          </FormLabel>
          <FormikDimensionedNumberInput
            className="w-full"
            label=""
            name={S.groundPrep.headlandWidthM.$path}
            dimensionedNumberInputProps={{
              storedUnit: "m",
              displayUnits: {
                imperial: "ft",
                metric: "m",
              },
              localSystem: measurementSystem,
              textFieldProps: {
                slotProps: {
                  htmlInput: {
                    min: 0,
                  },
                },
              },
            }}
            aria-labelledby="headland-width-label"
          />
        </FormControl>
        <FormControl>
          <FormLabel id="work-headlands-when-label">
            {t(
              "views.autotractor.jobCreation.step3ConfigurePreview.groundPrep.workHeadlandsWhen"
            )}
          </FormLabel>
          <Field
            component={FormikSelect}
            name={S.groundPrep.workHeadlandsWhen.$path}
            size="small"
            // work around alignment sloppiness from #4607
            formControl={{ className: "mt-0" }}
          >
            <MenuItem value="none">
              {t(
                "views.autotractor.jobCreation.step3ConfigurePreview.groundPrep.workHeadlandsNone"
              )}
            </MenuItem>
            <MenuItem value="before">
              {t(
                "views.autotractor.jobCreation.step3ConfigurePreview.groundPrep.workHeadlandsBefore"
              )}
            </MenuItem>
            <MenuItem value="after">
              {t(
                "views.autotractor.jobCreation.step3ConfigurePreview.groundPrep.workHeadlandsAfter"
              )}
            </MenuItem>
          </Field>
        </FormControl>
      </AccordionDetails>
    </Accordion>
  );
};
